import React, { useState, useCallback } from 'react';
import { Card, CardBody, Collapse, Badge, Spinner, Row, Col } from 'reactstrap';
import {
    Activity,
    Heart,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    AlertTriangle,
    Clock
} from 'lucide-react';
import axios from 'axios';
import MeasurementRow from './MeasurementRow';

const styles = {
    header: {
        background: '#ffffff',
        borderBottom: '1px solid #e3e6f0',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px'
    },
    bloodPressure: {
        background: '#fff8f8',
        borderLeft: '4px solid #e74a3b',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    button: {
        primary: {
            background: '#4e73df',
            color: '#ffffff',
            border: 'none',
            boxShadow: '0 2px 4px rgba(78,115,223,0.25)'
        }
    }
};

const SelfReportCard = ({ report, baseUrl, patientId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [details, setDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchDetails = useCallback(async () => {
        if (!patientId || !report.measurementId) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${baseUrl}web/patientSelfReportMeasurementDetails`, {
                patientId: patientId,
                measurementId: report.measurementId
            });

            setDetails(response.data);
        } catch (error) {
            console.error('Error fetching self report details:', error);
            setError('Failed to load measurement details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [patientId, report.measurementId, baseUrl]);

    const handleToggle = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        if (newIsOpen && !details && !error) {
            fetchDetails();
        }
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    const renderHealthMetric = (label, value, unit, color = 'primary') => (
        <div className={`d-inline-flex align-items-center me-3 px-3 py-2 bg-${color} bg-opacity-10 rounded`}>
            <Activity size={16} className={`text-${color} me-2`} />
            <div>
                <small className="text-muted d-block">{label}</small>
                <span className={`fw-semibold text-${color}`}>
                    {value} {unit}
                </span>
            </div>
        </div>
    );

    const renderBloodPressure = (bloodPressures) => {
        if (!bloodPressures?.length) return null;

        return (
            <Card className="mt-3" style={styles.bloodPressure}>
                <CardBody>
                    <div className="d-flex align-items-center mb-3">
                        <Heart size={18} className="text-danger me-2" />
                        <h6 className="mb-0 fw-bold">Blood Pressure</h6>
                    </div>
                    {bloodPressures.map((bp, index) => (
                        <div key={index} className="mb-2 bg-white border rounded p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 fw-bold text-danger">
                                    {bp.systolic}/{bp.diastolic}
                                    <small className="text-muted ms-2 fs-6 fw-normal">mmHg</small>
                                </h4>
                                <small className="text-muted">
                                    {formatTime(bp.createdAt)}
                                </small>
                            </div>
                        </div>
                    ))}
                </CardBody>
            </Card>
        );
    };

    return (
        <Card className="mb-4 border">
            <CardBody className="p-0">
                <div
                    className="p-3"
                    style={styles.header}
                    onClick={handleToggle}
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                                <Clock size={14} className="me-2" />
                                <h5 className="mb-0 me-3">{formatTime(report.createdAt)}</h5>
                                {report.isLastMeasurement && (
                                    <Badge color="primary" pill>Latest</Badge>
                                )}
                            </div>
                            <div className="d-flex align-items-center text-muted">
                                <Badge
                                    color='info'
                                    className="rounded-pill"
                                >
                                    {report.detectionType}
                                </Badge>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex flex-wrap gap-2">
                                {report.heartRate && renderHealthMetric('HR', report.heartRate, 'bpm', 'danger')}
                                {report.systolic && renderHealthMetric('BP', `${report.systolic}/${report.diastolic}`, 'mmHg', 'primary')}
                                {report.respiratoryRate && renderHealthMetric('RR', report.respiratoryRate, 'bpm', 'success')}
                            </div>
                            {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                    </div>
                </div>

                <Collapse isOpen={isOpen}>
                    <div className="p-3">
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner color="primary" size="sm" className="me-2" />
                                Loading measurement details...
                            </div>
                        ) : error ? (
                            <div className="text-center py-4 bg-light rounded">
                                <div className="text-danger mb-3">
                                    <AlertTriangle size={24} className="mb-2" />
                                    <div>{error}</div>
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={fetchDetails}
                                >
                                    <RefreshCw size={14} className="me-1" />
                                    Retry
                                </button>
                            </div>
                        ) : details && (
                            <>
                                <div className="mt-4">
                                    <MeasurementRow
                                        measurement={details.measurement}
                                        index={0}
                                    />
                                </div>

                                {renderBloodPressure(details.bloodPressures)}


                            </>
                        )}
                    </div>
                </Collapse>
            </CardBody>
        </Card>
    );
};

export default SelfReportCard;